import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Wallet,
  Repeat2,
  Car,
  Truck,
  FileText,
  MapPin,
  Check,
} from "lucide-react";
import api from "@/lib/api";
import CarCard from "@/components/site/CarCard";
import SearchBar from "@/components/site/SearchBar";
import ProcessTimeline from "@/components/site/ProcessTimeline";
import Seo from "@/components/site/Seo";
import { TID } from "@/constants/tids";

const HERO_IMG = "/homepage.jpg";

const ADVANTAGES = [
  {
    icon: ShieldCheck,
    text: "Garanție 12 luni",
    sub: "prelungibilă până la 3 ani",
  },
  {
    icon: Wallet,
    text: "Finanțare online",
    sub: "rapidă doar cu buletinul",
  },
  { icon: Repeat2, text: "Serviciu Buy Back", sub: "mașina ta la schimb" },
  { icon: Car, text: "Test Drive gratuit", sub: "programează oricând" },
  { icon: Truck, text: "Livrare în toată țara", sub: "acasă la tine" },
  {
    icon: FileText,
    text: "Documentație & înmatriculare",
    sub: "consultanță gratuită",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/vehicles/featured", { params: { limit: 6 } })
      .then((r) => setFeatured(r.data))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-[#0a0a0a]">
      <Seo
        title="AutoRIO — Parc Auto Suceava · Mașini second hand verificate"
        description="AutoRIO, dealer premium în Suceava: autoturisme second hand verificate tehnic, finanțare rapidă doar cu buletinul, garanție 12 luni, Buy Back, livrare în toată țara."
        path="/"
      />

      {/* ---------- HERO ---------- */}
      <section className="relative min-h-[92vh] md:min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="AutoRIO showroom"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-[60%_45%] md:object-[center_50%] scale-100 md:scale-90"
          />
          {/* Cinematic overlays — lighter, cars still readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/55 via-[#0a0a0a]/30 to-[#0a0a0a]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/85 via-[#0a0a0a]/25 to-transparent" />
          <div
            className="absolute inset-0 opacity-45 mix-blend-overlay pointer-events-none"
            style={{
              background:
                "radial-gradient(50% 40% at 30% 55%, rgba(212,175,55,0.28) 0%, transparent 70%)",
            }}
          />
        </div>

        <div className="relative container-wide pt-28 pb-24 md:pt-32 md:pb-28 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left: Text — takes 7/12 for more breathing room */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
              className="lg:col-span-7 3xl:ml-[calc((1760px_-_100vw)_/_2)]"
            >
              <div className="inline-flex items-center gap-2.5 rounded-full bg-white/[0.05] backdrop-blur-md pl-2 pr-5 py-1.5 border border-white/[0.1]">
                <span className="h-6 w-6 rounded-full bg-[#D4AF37] flex items-center justify-center">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#0a0a0a] animate-pulse" />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/85">
                  Dealer Premium · Suceava
                </span>
              </div>

              <h1 className="mt-7 font-display text-4xl sm:text-6xl lg:text-[80px] font-extrabold text-white leading-[0.98] tracking-tight">
                Cu noi{" "}
                <span className="font-serif-italic gold-gradient-text font-medium">
                  mergi
                </span>
                <br />
                mai departe<span className="text-[#D4AF37]">.</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
                Autoturisme atent selectate, verificate tehnic și pregătite
                pentru livrare — cu tot ceea ce ai nevoie ca să pleci liniștit
                la drum.
              </p>

              {/* Advantages list — 3x2 grid, more breathing room */}
              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 max-w-2xl">
                {ADVANTAGES.map((a, i) => (
                  <motion.li
                    key={a.text}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.35 + i * 0.06 }}
                    className="group flex items-start gap-3"
                  >
                    <span className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center group-hover:bg-[#D4AF37]/20 group-hover:border-[#D4AF37]/50 transition-colors">
                      <a.icon
                        className="h-4 w-4 text-[#D4AF37]"
                        strokeWidth={2.2}
                      />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[15px] font-bold text-white leading-tight">
                        {a.text}
                      </div>
                      <div className="text-[12px] text-white/50 mt-0.5">
                        {a.sub}
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-12 flex flex-wrap gap-3">
                <Link
                  to="/masini"
                  data-testid={TID.heroCtaCars}
                  className="gold-shine inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.55)]"
                >
                  Vezi stocul
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid={TID.heroCtaContact}
                  className="inline-flex items-center gap-2 bg-transparent border border-white/25 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors"
                >
                  Contactează-ne
                </Link>
              </div>
            </motion.div>

            {/* Right: single "De ce AutoRIO?" card, balanced and smaller */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
              className="hidden lg:flex lg:col-span-5 justify-end 3xl:-mt-24 3xl:mr-[calc((1760px_-_100vw)_/_2_+_30px)]"
            >
              <div className="relative w-full max-w-[420px] rounded-3xl border border-white/[0.08] bg-[#0a0a0a]/70 backdrop-blur-xl p-8 overflow-hidden">
                <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#D4AF37]/15 blur-3xl" />
                <div className="relative">
                  <div className="eyebrow">De ce AutoRIO</div>
                  <h3 className="mt-3 font-display text-2xl xl:text-[26px] font-extrabold text-white tracking-tight leading-tight">
                    De ce{" "}
                    <span className="font-serif-italic gold-gradient-text font-medium">
                      AutoRIO
                    </span>
                    ?
                  </h3>
                  <ul className="mt-6 space-y-4">
                    {[
                      "Mașini atent selectate",
                      "Verificare tehnică",
                      "Istoric verificat",
                      "Finanțare rapidă",
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-3">
                        <span className="h-7 w-7 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
                          <Check
                            className="h-3.5 w-3.5 text-[#D4AF37]"
                            strokeWidth={3}
                          />
                        </span>
                        <span className="text-white text-[15px] font-semibold">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/40 text-[10px] uppercase tracking-[0.24em]">
          <span>Explorează</span>
          <span className="h-8 w-px bg-gradient-to-b from-[#D4AF37] to-transparent animate-pulse" />
        </div>
      </section>

      {/* ---------- SEARCH BAR ---------- */}
      <section className="container-wide -mt-14 sm:-mt-16 relative z-20">
        <SearchBar />
      </section>

      {/* ---------- FEATURED CARS ---------- */}
      <section className="container-wide py-16 sm:py-24 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 sm:mb-14">
          <div>
            <div className="eyebrow">Selecția noastră</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              Mașini{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                recomandate
              </span>
            </h2>
            <p className="mt-3 text-white/50 max-w-lg text-base">
              Modele atent selectate, verificate tehnic și pregătite pentru
              livrare imediată.
            </p>
          </div>
          <Link
            to="/masini"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#D4AF37] hover:text-[#e8c86a] transition-colors"
          >
            Vezi toate mașinile
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {featured.map((c, i) => (
            <CarCard key={c.id} car={c} index={i} />
          ))}
        </div>

        {featured.length === 0 && (
          <div className="text-center py-16 text-white/40 text-sm">
            În curând vom lista mașini noi în această secțiune.
          </div>
        )}
      </section>

      {/* Process timeline (unchanged) */}
      <ProcessTimeline />

      {/* CTA */}
      <section className="container-wide py-16 sm:py-24">
        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.06] bg-gradient-to-br from-[#141210] via-[#0e0d0b] to-[#0a0a0a] px-6 py-14 sm:px-12 sm:py-16 md:px-20 md:py-24">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-[#D4AF37]/8 blur-3xl" />
          <div className="relative max-w-2xl">
            <div className="eyebrow">Ai găsit o mașină?</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              Ofertă de finanțare{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                personalizată
              </span>{" "}
              în 24h
            </h2>
            <p className="mt-4 text-white/60 text-base sm:text-lg leading-relaxed">
              Trimite câteva detalii și te contactăm cu o ofertă adaptată
              nevoilor tale, prin partenerii noștri financiari.
            </p>
            <Link
              to="/finantare"
              className="gold-shine mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0a0a0a] hover:bg-[#e8c86a] rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]"
            >
              Solicită ofertă
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
